import { isPlainObject } from 'client/helper/object-helper';

/** Policy for the instant-checkup `extraData` bag — see ./CLAUDE.md */
export enum CaptureIntent {
  NEW = 'NEW',
  RETAKE = 'RETAKE',
  SIDE_FACE_NUDGE = 'SIDE_FACE_NUDGE',
}

export enum CaptureMedium {
  CAMERA = 'CAMERA',
  GALLERY = 'GALLERY',
}

/** Which screen launched the capture — an analytics dimension, not a behaviour switch. */
export enum CapturePage {
  Instruction = 'Instruction',
  ConsultationQuiz = 'consultationQuiz',
}

export interface CaptureContext {
  [key: string]: unknown;
  tag?: string;
  consultationSessionId?: string;
  followUpId?: string;
  supportTicketId?: string;
  messageId?: string;
  className?: string;
  isBothSideFacePhotoFlowEnabled?: boolean;
  isUploadedFromGallery?: boolean;
}

const TRANSPORT_FIELDS: Array<string> = ['nativeCameraFile', 'extraData'];
const INTENTS_DROPPING_TAG: ReadonlySet<CaptureIntent> = new Set([CaptureIntent.RETAKE, CaptureIntent.SIDE_FACE_NUDGE]);
const INTENTS_DROPPING_MESSAGE_ID: ReadonlySet<CaptureIntent> = new Set([CaptureIntent.SIDE_FACE_NUDGE]);

export function buildExtraData(
  intent: CaptureIntent, context: CaptureContext, medium: CaptureMedium = CaptureMedium.CAMERA,
): CaptureContext {
  const extraData: CaptureContext = { ...context };
  TRANSPORT_FIELDS.forEach((field: string): void => { delete extraData[field]; });
  if (INTENTS_DROPPING_TAG.has(intent)) delete extraData.tag;
  if (INTENTS_DROPPING_MESSAGE_ID.has(intent)) delete extraData.messageId;
  if (medium === CaptureMedium.GALLERY) extraData.isUploadedFromGallery = true;
  else delete extraData.isUploadedFromGallery;
  return extraData;
}

export function mergeContext(persisted: CaptureContext = {}, incoming: CaptureContext = {}): CaptureContext {
  return { ...persisted, ...incoming };
}

export function parseContext(raw: unknown): CaptureContext {
  if (!raw) return {};
  if (typeof raw === 'string') return tryParseContext(raw) ?? {};
  return isPlainObject(raw) ? { ...raw } : {};
}

/** Like `parseContext` but tells unreadable apart from empty, for callers that must refuse to launch. */
export function tryParseContext(raw: string): CaptureContext | undefined {
  try {
    const parsed: unknown = JSON.parse(raw);
    return isPlainObject(parsed) ? { ...parsed } : undefined;
  } catch {
    return undefined;
  }
}
